import React from 'react'
import PropTypes from 'prop-types'
import {Tooltip} from 'antd'
import {barPercentOf, belowRatioOf, efficiencyToneOf} from '../../helpers/efficiencyReport'
import {DASH, formatEfficiency, formatInt, formatPercent} from '../../helpers/format'

/**
 * Biểu đồ "Hiệu suất theo công đoạn" — màn THEO CÔNG ĐOẠN.
 *
 * Thanh NGANG chứ không cột dọc: nhãn là tên công đoạn tiếng Việt có dấu ("Hoàn thiện", "Đóng gói"),
 * và tên dài nằm ngang đọc được, còn xoay 45° dưới trục cột thì không.
 *
 * Mỗi hàng là một công đoạn: thanh đặc = kỳ này, vệt nhạt phía sau = kỳ trước. Hai lớp chồng nhau
 * nên thanh kỳ này có viền 2px MÀU NỀN tách khỏi vệt sau — tách bằng khoảng trống, không bằng nét
 * vẽ thêm (nét là mực không mang dữ liệu).
 *
 * ⚠️ Trục ngang CỐ ĐỊNH 0–100% (không co theo dữ liệu). Hiệu suất là tỷ lệ với 100% mang nghĩa
 *    thật, và trục co theo max biến một công đoạn 42% thành một thanh gần kín khung — người đọc
 *    lướt qua sẽ hiểu ngược hẳn. Công đoạn vượt 100% bị kẹp về mép và nói rõ bằng con số ở mũi.
 *
 * Giá trị in ở MŨI từng thanh (nhãn trực tiếp), nên không cần lưới dày — chỉ ba vạch mốc hairline
 * ở các ngưỡng xếp hạng để mắt neo vào.
 */
function StageEfficiencyChart({stages, thresholds, previousMedians, belowLabel}) {
    const rows = (stages || [])
        .map((stage) => ({
            code: stage.stageCode,
            name: stage.name || stage.stageCode,
            efficiency: stage.efficiency,
            median: stage.efficiency && stage.efficiency.distribution ? stage.efficiency.distribution.median : null,
            previous: previousMedians ? previousMedians[stage.stageCode] : null,
        }))
        .filter((row) => row.efficiency && !row.efficiency.notApplicable)
        // Thấp nhất LÊN ĐẦU: biểu đồ này tồn tại để chỉ ra chỗ cần can thiệp, nên chỗ đó phải là
        // thứ đọc được mà không cần cuộn.
        .sort((a, b) => (a.median === null ? 1 : b.median === null ? -1 : a.median - b.median))

    if (rows.length === 0) return null

    const hasPrevious = rows.some((row) => typeof row.previous === 'number')

    return (
        <div className="VcStageChart">
            <div className="VcStageChartHead">
                Hiệu suất theo công đoạn
                <span className="VcMuted"> — trung vị, thấp nhất lên đầu</span>
                {hasPrevious && (
                    <span className="VcStageLegend">
                        <i className="VcLegendKey VcLegendKey--now"/> Kỳ này
                        <i className="VcLegendKey VcLegendKey--prev"/> Kỳ trước
                    </span>
                )}
            </div>

            <div className="VcStageChartBody">
                {/*
                  * Vạch mốc đúng ở ba ngưỡng xếp hạng, không phải ở 25/50/75%: người đọc cần thấy
                  * thanh này nằm bên nào của ngưỡng "Đạt", còn một phần tư trục thì không mang
                  * nghĩa nghiệp vụ nào.
                  */}
                <div className="VcStageGrid" aria-hidden="true">
                    {[thresholds.c, thresholds.b, thresholds.a].map((value) => (
                        <span className="VcStageGridLine" key={value} style={{left: `${barPercentOf(value)}%`}}>
                            {/*
                              * ⚠️ Vạch mốc in NGƯỠNG cấu hình, nên 0 chữ số thập phân — đúng theo
                              *    docblock của `formatEfficiency`. Dùng nhầm hàm kia thì vạch ghi
                              *    "90,0%" trong khi chân bộ lọc cách đó vài chục pixel ghi "A ≥ 90%".
                              */}
                            <em>{formatPercent(value, 0)}</em>
                        </span>
                    ))}
                </div>

                {rows.map((row) => {
                    const dist = row.efficiency.distribution
                    const tone = efficiencyToneOf(row.median, thresholds)
                    // Cặp tử/mẫu dựng ở `belowRatioOf` — một chỗ cho cả hai màn, xem docblock ở đó.
                    const ratio = belowRatioOf(dist)
                    const below = ratio ? ratio.below : 0
                    const sample = ratio ? ratio.total : 0

                    return (
                        <div className="VcStageRow" key={row.code}>
                            <span className="VcStageName" title={row.name}>{row.name}</span>
                            <div className="VcStageTrack">
                                {typeof row.previous === 'number' && (
                                    <Tooltip title={`Kỳ trước: ${formatEfficiency(row.previous)}`}>
                                        <span className="VcStageBar VcStageBar--prev" style={{width: `${barPercentOf(row.previous)}%`}}/>
                                    </Tooltip>
                                )}
                                {row.median === null ? null : (
                                    <Tooltip title={tooltipOf(row, sample, below, belowLabel)}>
                                        <span
                                            className={`VcStageBar VcStageBar--now VcStageBar--${tone || 'D'}`}
                                            style={{width: `${barPercentOf(row.median)}%`}}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                            <span className="VcStageValue">
                                {row.median === null ? DASH : formatEfficiency(row.median)}
                                <Delta current={row.median} previous={row.previous}/>
                            </span>
                            {/*
                              * Số người dưới ngưỡng đi kèm ngay hàng, không chờ tooltip: đây là con
                              * số quyết định có cần can thiệp hay không, và một con số chỉ hiện khi
                              * hover thì trên màn hình xưởng coi như không tồn tại.
                              */}
                            <span className={below > 0 ? 'VcStageBelow VcWarning' : 'VcStageBelow VcMuted'}>
                                {sample > 0 ? `${formatInt(below)}/${formatInt(sample)} dưới ngưỡng` : 'mẫu quá mỏng'}
                            </span>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

const tooltipOf = (row, sample, below, belowLabel) => {
    const parts = [`Trung vị ${formatEfficiency(row.median)}`]

    if (row.efficiency.distribution && row.efficiency.distribution.p10 !== null) {
        parts.push(`P10–P90 ${formatEfficiency(row.efficiency.distribution.p10)}–${formatEfficiency(row.efficiency.distribution.p90)}`)
    }

    parts.push(`${formatInt(below)}/${formatInt(sample)} ${belowLabel}`)

    if (typeof row.previous === 'number') parts.push(`kỳ trước ${formatEfficiency(row.previous)}`)

    return parts.join(' · ')
}

/** Chênh so kỳ trước, tính bằng ĐIỂM phần trăm — hiệu của hai tỷ lệ, không phải tỷ lệ của tỷ lệ. */
const Delta = ({current, previous}) => {
    if (current === null || typeof previous !== 'number') return null

    const delta = current - previous

    if (Math.abs(delta) < 0.01) return <span className="VcMuted VcStageDelta">đi ngang</span>

    return (
        <span className={delta > 0 ? 'VcStageDelta VcSuccess' : 'VcStageDelta VcDanger'}>
            {delta > 0 ? '▲' : '▼'}{formatInt(Math.round(Math.abs(delta) * 100))}
        </span>
    )
}

StageEfficiencyChart.propTypes = {
    stages: PropTypes.array,
    thresholds: PropTypes.object.isRequired,
    previousMedians: PropTypes.object,
    belowLabel: PropTypes.string,
}

StageEfficiencyChart.defaultProps = {
    belowLabel: 'dưới ngưỡng',
}

export default StageEfficiencyChart
